<template>
	<div class="mall-shop-page">
		<div v-loading="loading" class="shop-panel">
			<div class="page-header">
				<div>
					<h3 class="page-title">申请详情</h3>
					<div class="page-sub">查看店铺申请资料、审核状态和审核意见。</div>
				</div>
				<div>
					<el-button @click="$router.push('/mall/index')">返回商城</el-button>
					<el-button v-if="canResubmit" type="primary" @click="$router.push('/mall/shop/apply')">重新提交</el-button>
				</div>
			</div>

			<template v-if="applyInfo.id">
				<div class="section">
					<div class="section-title">审核进度</div>
					<div class="status-row">
						<el-tag :type="auditStateTagType">{{ auditStateText }}</el-tag>
						<span>{{ applyTypeText }}</span>
					</div>
					<div v-if="applyInfo.auditOpinion" class="close-warning" style="margin-top: 16px;">
						审核意见：{{ applyInfo.auditOpinion }}
					</div>
				</div>

				<div class="section">
					<div class="section-title">申请资料</div>
					<div class="info-grid">
						<div class="info-item">
							<span class="info-label">店铺名称：</span>
							<span class="info-value">{{ applyInfo.shopName || '-' }}</span>
						</div>
						<div class="info-item">
							<span class="info-label">联系人：</span>
							<span class="info-value">{{ applyInfo.contactPerson || applyInfo.contactName || '-' }}</span>
						</div>
						<div class="info-item">
							<span class="info-label">联系方式：</span>
							<span class="info-value">{{ applyInfo.contactPhone || applyInfo.telephone || '-' }}</span>
						</div>
						<div class="info-item">
							<span class="info-label">经营主体：</span>
							<span class="info-value">{{ applyInfo.companyName || applyInfo.subjectName || '-' }}</span>
						</div>
						<div class="info-item full">
							<span class="info-label">经营地址：</span>
							<span class="info-value">{{ applyInfo.businessAddress || applyInfo.address || '-' }}</span>
						</div>
						<div class="info-item full">
							<span class="info-label">业务简介：</span>
							<span class="info-value">{{ applyInfo.businessDesc || applyInfo.businessScope || '-' }}</span>
						</div>
						<div class="info-item">
							<span class="info-label">开户行：</span>
							<span class="info-value">{{ applyInfo.bankName || '-' }}</span>
						</div>
						<div class="info-item">
							<span class="info-label">开户账号：</span>
							<span class="info-value">{{ applyInfo.bankAccount || '-' }}</span>
						</div>
					</div>
				</div>

				<div class="section">
					<div class="section-title">申请附件</div>
					<div v-if="fileList.length" class="attachment-groups">
						<div class="attachment-group">
							<div class="attachment-group-label">店铺 Logo：</div>
							<div v-if="shopLogoFiles.length" class="attachment-list">
								<el-image
									v-for="item in shopLogoFiles"
									:key="item.fileId"
									class="attachment-image"
									:src="item.url"
									:preview-src-list="imagePreviewList"
									fit="cover"
								></el-image>
							</div>
							<span v-else class="empty-tip">暂无</span>
						</div>
						<div class="attachment-group">
							<div class="attachment-group-label">营业执照：</div>
							<div v-if="businessLicenseFiles.length" class="attachment-list">
								<template v-for="item in businessLicenseFiles">
									<el-image
										v-if="item.isImage"
										:key="item.fileId"
										class="attachment-image"
										:src="item.url"
										:preview-src-list="imagePreviewList"
										fit="cover"
									></el-image>
									<div v-else :key="item.fileId" class="file-chip" @click="handlePreviewFile(item)">
										<i class="el-icon-document"></i>
										<span>{{ item.name }}</span>
									</div>
								</template>
							</div>
							<span v-else class="empty-tip">暂无</span>
						</div>
					</div>
					<div v-else class="empty-tip">暂无附件</div>
				</div>
			</template>

			<empty-content v-else empty-text="暂无申请信息"></empty-content>
		</div>
	</div>
</template>

<script>
	import EmptyContent from '@/components/EmptyContent'
	import { previewMerchantFile, queryMerchantFileList, queryMerchantShopApplyDetail, queryMyLatestShopApply } from '@/api/mall'
	import { ENUM_SHOP_APPLY_TYPE, ENUM_SHOP_AUDIT_STATE, SHOP_AUDIT_STATE } from '@/enum/index.js'

	export default {
		name: 'MallShopApplyDetail',
		components: { EmptyContent },
		data() {
			return {
				loading: false,
				applyInfo: {},
				fileList: []
			}
		},
		computed: {
			auditStateText() {
				return ENUM_SHOP_AUDIT_STATE[this.applyInfo.auditState] || '未知状态'
			},
			auditStateTagType() {
				if (Number(this.applyInfo.auditState) === SHOP_AUDIT_STATE.PASS) return 'success'
				if (Number(this.applyInfo.auditState) === SHOP_AUDIT_STATE.REJECT) return 'danger'
				return 'warning'
			},
			applyTypeText() {
				return ENUM_SHOP_APPLY_TYPE[this.applyInfo.applyType] || '店铺申请'
			},
			canResubmit() {
				return Number(this.applyInfo.auditState) === SHOP_AUDIT_STATE.REJECT && Number(this.applyInfo.applyType) === 1
			},
			shopLogoFiles() {
				return this.fileList.filter(item => item.fileBusinessType === 'SHOP_LOGO')
			},
			businessLicenseFiles() {
				return this.fileList.filter(item => item.fileBusinessType === 'BUSINESS_LICENSE')
			},
			imagePreviewList() {
				return this.fileList.filter(item => item.isImage && item.url).map(item => item.url)
			}
		},
		created() {
			this.initPage()
		},
		methods: {
			async initPage() {
				this.loading = true
				try {
					const id = this.$route.query.id
					const res = id ? await queryMerchantShopApplyDetail({ id }) : await queryMyLatestShopApply()
					this.applyInfo = res.data || {}
					this.fileList = this.normalizeFiles(this.applyInfo.merchantFiles)
					if (this.applyInfo.id && !this.fileList.length) {
						await this.loadFiles(this.applyInfo.id)
					}
				} finally {
					this.loading = false
				}
			},
			async loadFiles(businessId) {
				const res = await queryMerchantFileList({ businessId }).catch(() => ({ data: [] }))
				this.fileList = this.normalizeFiles(res.data)
			},
			normalizeFiles(files) {
				if (!Array.isArray(files)) return []
				return files.map(item => {
					const fileId = item.id || item.fileId || item.fileRecordId
					const name = item.fileName || item.name || item.originalName || '附件'
					const fileBusinessType = item.fileBusinessType
					return {
						fileId,
						name,
						fileBusinessType,
						url: fileId ? previewMerchantFile() + fileId : '',
						isImage: this.isImageFile({ ...item, name, fileBusinessType })
					}
				})
			},
			isImageFile(file) {
				const fileName = String(file.name || file.fileName || file.originalName || '').toLowerCase()
				const fileType = String(file.fileType || file.contentType || file.mimeType || '').toLowerCase()
				const imageExts = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp']
				if (fileType.includes('image')) return true
				if (imageExts.some(ext => fileName.endsWith(ext))) return true
				return ['SHOP_LOGO', 'BUSINESS_LICENSE'].includes(file.fileBusinessType)
			},
			handlePreviewFile(item) {
				if (!item.fileId) return
				window.open(previewMerchantFile() + item.fileId, '_blank')
			}
		}
	}
</script>

<style lang="scss" scoped>
	@import './shop.scss';
</style>
